import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import Icon from './Icon'

// Brand pin-check marker — avoids Leaflet's bundler-broken default marker images
const pinIcon = L.divIcon({
  className: '',
  html: `<svg width="40" height="40" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
    <path d="M60 8 C36 8 18 26 18 50 C18 82 60 114 60 114 C60 114 102 82 102 50 C102 26 84 8 60 8 Z" fill="#4fdbc8" stroke="#0e1513" stroke-width="4"/>
    <path d="M42 50 L55 65 L80 34" fill="none" stroke="#0e1513" stroke-width="12" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
  iconSize: [40, 40],
  iconAnchor: [20, 38],
})

const deviceDot = L.divIcon({
  className: '',
  html: `<div style="width:16px;height:16px;border-radius:50%;background:#4fdbc8;border:3px solid #fff;box-shadow:0 0 0 6px rgba(79,219,200,0.25)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
})

const DEFAULT_CENTER = [-33.8688, 151.2093] // Sydney fallback

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18`,
      { headers: { Accept: 'application/json' } }
    )
    if (!res.ok) return null
    const data = await res.json()
    return data.display_name || null
  } catch {
    return null
  }
}

export default function LocationPicker({ onConfirm }) {
  // 'ask' → consent prompt · 'map' → picking on map
  const [phase, setPhase] = useState('ask')
  const [isLocating, setIsLocating] = useState(false)
  const [geoError, setGeoError] = useState('')
  const [pin, setPin] = useState(null) // {lat, lng}
  const [address, setAddress] = useState('')
  const [isResolving, setIsResolving] = useState(false)

  const mapEl = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const geocodeTimer = useRef(null)

  const placePin = (lat, lng, { fly = false } = {}) => {
    setPin({ lat, lng })

    const map = mapRef.current
    if (map) {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng])
      } else {
        markerRef.current = L.marker([lat, lng], { icon: pinIcon, draggable: true }).addTo(map)
        markerRef.current.on('dragend', () => {
          const p = markerRef.current.getLatLng()
          placePin(p.lat, p.lng)
        })
      }
      if (fly) map.flyTo([lat, lng], 17, { duration: 0.8 })
    }

    // Debounced reverse geocode so dragging doesn't spam the service
    setIsResolving(true)
    clearTimeout(geocodeTimer.current)
    geocodeTimer.current = setTimeout(async () => {
      const found = await reverseGeocode(lat, lng)
      setAddress(found || `${lat.toFixed(6)}, ${lng.toFixed(6)}`)
      setIsResolving(false)
    }, 600)
  }

  // Init map when entering map phase
  useEffect(() => {
    if (phase !== 'map' || mapRef.current || !mapEl.current) return

    const map = L.map(mapEl.current, { zoomControl: true }).setView(DEFAULT_CENTER, 13)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      maxZoom: 20,
    }).addTo(map)

    map.on('click', (e) => placePin(e.latlng.lat, e.latlng.lng))
    mapRef.current = map

    return () => {
      clearTimeout(geocodeTimer.current)
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const useDeviceLocation = () => {
    setGeoError('')
    setIsLocating(true)

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords
        setIsLocating(false)
        setPhase('map')
        // Map mounts on next tick — then drop the device dot and pin
        setTimeout(() => {
          const map = mapRef.current
          if (map) {
            L.marker([latitude, longitude], { icon: deviceDot, interactive: false }).addTo(map)
            map.setView([latitude, longitude], 17)
          }
          placePin(latitude, longitude)
        }, 50)
      },
      (err) => {
        setIsLocating(false)
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission was denied. You can still pin the spot on the map manually.'
            : 'Could not get your location. You can still pin the spot on the map manually.'
        )
        setPhase('map')
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    )
  }

  // ── Phase 1: consent ──
  if (phase === 'ask') {
    return (
      <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="bg-accent/15 border border-accent/20 p-2.5 rounded-xl shrink-0">
            <Icon name="my_location" className="text-accent text-xl" />
          </div>
          <div className="space-y-1">
            <h3 className="text-label-md text-text-primary">Use your device location?</h3>
            <p className="text-body-md text-text-secondary">
              We'll center the map on where you are so you can pin the exact spot. Your location is
              only captured for this report — it is never tracked in the background or shared for
              any other purpose.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setPhase('map')}
            className="flex-1 border border-border text-text-primary text-label-md rounded-xl py-3 hover:bg-surface-high transition"
          >
            No, pick on map
          </button>
          <button
            onClick={useDeviceLocation}
            disabled={isLocating}
            className="flex-1 flex items-center justify-center gap-2 bg-accent text-surface-dark font-semibold text-label-md rounded-xl py-3 hover:bg-accent-light transition disabled:opacity-50"
          >
            <Icon name="my_location" className="text-lg" />
            {isLocating ? 'Locating…' : 'Allow this once'}
          </button>
        </div>
      </div>
    )
  }

  // ── Phase 2: map picking ──
  return (
    <div className="space-y-3">
      {geoError && (
        <div className="flex items-start gap-2 bg-status-pending/10 border border-status-pending/40 rounded-xl p-3">
          <Icon name="info" className="text-status-pending text-lg shrink-0 mt-0.5" />
          <p className="text-label-md text-text-secondary">{geoError}</p>
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-border relative">
        <div ref={mapEl} className="h-72 md:h-80 w-full" />
        <button
          onClick={useDeviceLocation}
          disabled={isLocating}
          title="Center on my location"
          className="absolute bottom-3 right-3 z-[1000] bg-surface border border-border rounded-full p-3 min-h-0 shadow-lg hover:border-accent transition"
        >
          <Icon name="my_location" className={`text-xl ${isLocating ? 'text-text-muted' : 'text-accent'}`} />
        </button>
      </div>

      <p className="flex items-center gap-1.5 text-label-sm text-text-muted">
        <Icon name="info" className="text-sm" />
        Tap the map or drag the pin to refine the exact location.
      </p>

      {pin && (
        <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Icon name="location_on" className="text-accent text-xl shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-label-md text-text-primary break-words">
                {isResolving ? 'Finding address…' : address}
              </p>
              <p className="text-label-sm text-text-muted font-mono">
                {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
              </p>
            </div>
          </div>
          <button
            onClick={() => onConfirm({ lat: pin.lat, lng: pin.lng, address })}
            disabled={isResolving}
            className="w-full flex items-center justify-center gap-2 bg-accent text-surface-dark font-semibold text-label-md rounded-xl py-3 hover:bg-accent-light transition disabled:opacity-50"
          >
            <Icon name="check" className="text-lg" />
            Confirm this location
          </button>
        </div>
      )}
    </div>
  )
}
